import {capturePopupSave, popupSaved} from "./popup-refresh";
import Dropzone from 'dropzone';
import {receipt} from "./receipts";
import {fancybox} from "./fancybox";
import {Notifications} from "./notifications";
import utils from "../utils/utils.js";
import {toolTip} from "./tooltip";
import {liveUpdateMutationBridge} from "./liveUpdateMutationBridge";
import {
    createSupportTicketMutationId,
    isSupportTicketFirebaseId,
    supportTicketReloadRequiredMessage,
} from "../helpers/support-ticket-admin-cloud";
import {supportTicketMutationQueue} from "../helpers/support-ticket-mutation-queue";
import {userMessage} from "../helpers/user-message";
import {findSupportTicketPopup} from "../helpers/support-ticket-popup";

Dropzone.autoDiscover = false;

const s = {
    // Begge, med vilje. Markuppen er cloud-renderet
    // (support_ticket_popup_render.js), og den udsender nu BÅDE data-dropzone
    // og klassen. Indtil cloud-grenen er deployet, findes kun klassen ude i
    // produktion — vælger vi udelukkende attributten, holder filupload op med
    // at virke i deploy-vinduet. .dropzone fjernes her i oprydningen (trin 9),
    // sammen med de øvrige klasser cloud stadig udsender.
    dropZone: '[data-dropzone], .dropzone',
    triggerUpload: '.trigger-upload',
    filesTable: '[data-files]',
    awsAction: '[data-aws-action]',
    awsDelete: '[data-aws-delete]',
}

const methods = {
    click: 'click',
}

const msg = utils.get_js_translations();

const a = {
    wl_upload_file_data: 'wl_upload_file_data',
    wl_get_aws_pre_signed_url: 'wl_get_aws_pre_signed_url',
    wl_delete_aws_file: 'wl_delete_aws_file',
}

let fileUploadEventsBound = false;

export default class FileUpload {
    constructor(root = document) {
        if (!document.querySelector(s.dropZone)) return;

        this.fancybox = fancybox;
        this.notyf = new Notifications();
        this.pendingUploadContexts = new Map();
        new toolTip();

        // Bind delegated events only once (this class can be instantiated multiple times)
        if (!fileUploadEventsBound) {
            this.eventHandlers();
            fileUploadEventsBound = true;
        }

        this.initDropzone(root);
    }

    eventHandlers() {
        $(document.body).on(methods.click, s.awsAction, this.awsAction.bind(this));
        $(document.body).on(methods.click, s.awsDelete, this.awsDelete.bind(this));
    }

    destroyDropZones(root = document) {
        const rootElement = root instanceof jQuery ? root.get(0) : root;
        if (!rootElement) return;

        // Iterate over a copy because Dropzone removes each destroyed instance from this array.
        Dropzone.instances.slice().forEach(instance => {
            if (instance.element === rootElement || rootElement.contains(instance.element)) {
                instance.destroy();
            }
        });
    }

    awsDelete(e) {
        const _this = $(e.currentTarget);
        if (this.isSupportTicketFormSaving(_this)) {
            this.notyf.info(msg.loading);
            return;
        }

        const ticketState = this.getTicketState(_this);
        const attachmentId = String(_this.data('attachment-id') || '').trim().toLowerCase();
        if (!ticketState || !isSupportTicketFirebaseId(attachmentId)) {
            this.notyf.error(msg.file_error);
            return;
        }

        const popupSave = capturePopupSave(ticketState.table);
        supportTicketMutationQueue.enqueue(
            ticketState.firebaseId,
            () => this.deleteFile(_this, attachmentId, ticketState.firebaseId, popupSave)
        ).catch((error) => {
            this.notyf.removeAll();
            this.notyf.error(this.getErrorMessage(error));
        });
    }

    async deleteFile(trigger, attachmentId, firebaseId, popupSave) {
        const triggerElement = trigger.get(0);
        if (!triggerElement || !triggerElement.isConnected) return;

        const tr = trigger.parents('tr');
        const ticketState = this.getTicketState(trigger);
        if (!ticketState || ticketState.firebaseId !== firebaseId) {
            this.notyf.error(msg.file_error);
            return;
        }

        const contextDataKey = 'support-ticket-delete-context';
        let deleteContext = trigger.data(contextDataKey);
        if (
            !deleteContext ||
            deleteContext.firebase_id !== ticketState.firebaseId ||
            deleteContext.attachment_id !== attachmentId ||
            !isSupportTicketFirebaseId(deleteContext.mutation_id)
        ) {
            deleteContext = {
                attachment_id: attachmentId,
                firebase_id: ticketState.firebaseId,
                expected_revision: ticketState.revision,
                mutation_id: createSupportTicketMutationId(),
                page_post_id: utils.get_post_id(),
            };
            trigger.data(contextDataKey, deleteContext);
        }

        this.notyf.info(msg.deleting_file);
        let response;
        try {
            response = await this.requestWithRetry({
                url: utils.get_ajax_url(),
                type: 'POST',
                data: {
                    action: a.wl_delete_aws_file,
                    ...deleteContext,
                    _wpnonce: utils.get_nonce(),
                },
            });
        } catch (request) {
            this.notyf.removeAll();
            const compensationResponse = request?.responseJSON;
            if (
                compensationResponse?.record_synced === true &&
                this.isValidTicketStateResponse(compensationResponse, ticketState, attachmentId)
            ) {
                trigger.removeData(contextDataKey);
                this.applyTicketMutationState(compensationResponse, ticketState.table, popupSave);
                this.notyf.error(this.getErrorMessage(compensationResponse));
                return;
            }

            if (!this.isRetryableRequestFailure(request)) {
                trigger.removeData(contextDataKey);
            }
            this.notyf.error(this.getErrorMessage(request));
            return;
        }

        this.notyf.removeAll();
        if (this.isValidTicketMutationResponse(response, deleteContext, 'delete_attachment') && response.is_deleted) {
            trigger.removeData(contextDataKey);
            this.applyTicketMutationState(response, ticketState.table, popupSave);
            tr.remove();
            return;
        }

        if (
            response?.record_synced === true &&
            this.isValidTicketStateResponse(response, ticketState, attachmentId)
        ) {
            this.applyTicketMutationState(response, ticketState.table, popupSave);
        }
        if (response?.success === false) {
            trigger.removeData(contextDataKey);
        }
        this.notyf.error(this.getErrorMessage(response));
    }

    getTicketState(scope) {
        const container = findSupportTicketPopup(scope);
        const table = container.find(s.filesTable).first();
        const firebaseId = String(table.attr('data-firebase-id') || '').trim().toLowerCase();
        const revision = Number.parseInt(table.attr('data-revision'), 10);
        const reloadRequired = table.attr('data-reload-required') === '1';
        if (reloadRequired || !table.length || !isSupportTicketFirebaseId(firebaseId) || !Number.isInteger(revision) || revision <= 0) {
            return null;
        }

        return {table, firebaseId, revision};
    }

    isSupportTicketFormSaving(scope) {
        return findSupportTicketPopup(scope)
            .find('[data-form-support-ticket] [type="submit"].processing')
            .length > 0;
    }

    applyTicketMutationState(response, table, popupSave) {
        popupSaved(table, response, popupSave);
        if (response?.reload_required === true) {
            this.markTicketReloadRequired(response, table);
            return;
        }

        this.applyTicketMutationResponse(response, table);
    }

    applyTicketMutationResponse(response, table) {
        const firebaseId = String(response?.firebase_id || '').trim().toLowerCase();
        const revision = Number.parseInt(response?.revision, 10);
        if (isSupportTicketFirebaseId(firebaseId) && Number.isInteger(revision) && revision > 0 && table?.length) {
            table.attr('data-firebase-id', firebaseId);
            table.attr('data-revision', revision);
            table.data('firebase-id', firebaseId);
            table.data('revision', revision);
            findSupportTicketPopup(table).find('[name="expected_revision"]').val(revision);
        }

        if (response?.live_update_context?.post_type === 'support_ticket') {
            liveUpdateMutationBridge.trigger(response.live_update_context);
        }
    }

    markTicketReloadRequired(response, table) {
        const alreadyMarked = table.attr('data-reload-required') === '1';
        table.attr('data-reload-required', '1');

        const form = findSupportTicketPopup(table).find('[data-form-support-ticket]').first();
        form.attr('data-support-ticket-reload-required', '1');
        form.find(':input').prop('disabled', true);

        if (response?.live_update_context?.post_type === 'support_ticket') {
            liveUpdateMutationBridge.trigger(response.live_update_context);
        }
        if (!alreadyMarked) {
            receipt.error(supportTicketReloadRequiredMessage);
        }
    }

    isValidTicketMutationResponse(response, mutation, action) {
        const firebaseId = String(response?.firebase_id || '').trim().toLowerCase();
        const mutationId = String(response?.mutation_id || '').trim().toLowerCase();
        const attachmentId = String(response?.attachment?.attachment_id || '').trim().toLowerCase();
        const revision = Number.parseInt(response?.revision, 10);
        const ticketNumber = Number.parseInt(response?.ticket_number, 10);

        return response?.success === true
            && response?.action === action
            && firebaseId === mutation.firebase_id
            && mutationId === mutation.mutation_id
            && attachmentId === mutation.attachment_id
            && Number.isInteger(revision)
            && revision > 0
            && Number.isInteger(ticketNumber)
            && ticketNumber > 0
            && typeof response?.reload_required === 'boolean'
            && response?.live_update_context?.post_type === 'support_ticket';
    }

    isValidTicketStateResponse(response, ticketState, attachmentId) {
        const firebaseId = String(response?.firebase_id || '').trim().toLowerCase();
        const responseAttachmentId = String(response?.attachment?.attachment_id || '').trim().toLowerCase();
        const revision = Number.parseInt(response?.revision, 10);
        const ticketNumber = Number.parseInt(response?.ticket_number, 10);

        return firebaseId === ticketState.firebaseId
            && responseAttachmentId === attachmentId
            && Number.isInteger(revision)
            && revision > 0
            && Number.isInteger(ticketNumber)
            && ticketNumber > 0
            && typeof response?.reload_required === 'boolean'
            && response?.live_update_context?.post_type === 'support_ticket';
    }

    isRetryableRequestFailure(request) {
        const status = Number.parseInt(request?.status, 10) || 0;
        return [0, 408, 429, 500, 502, 503, 504].includes(status);
    }

    requestWithRetry(settings, attemptsRemaining = 2) {
        return new Promise((resolve, reject) => {
            $.ajax({
                ...settings,
                success: resolve,
                error: (request) => {
                    if (attemptsRemaining > 1 && this.isRetryableRequestFailure(request)) {
                        this.requestWithRetry(settings, attemptsRemaining - 1).then(resolve).catch(reject);
                        return;
                    }

                    reject(request);
                },
            });
        });
    }

    awsAction(e) {
        const _this_parent = this;
        const fancybox = _this_parent.fancybox;
        const _this = $(e.currentTarget);
        const type = _this.data('type');

        jQuery.ajax({
            url:
                utils.get_ajax_url(),
            type: 'POST',
            beforeSend: function () {
                _this_parent.notyf.info(msg.awaiting_response);
            },
            data: {
                action: a.wl_get_aws_pre_signed_url,
                file_name: _this.data('file-name'),
                file_type: _this.data('type'),
                _wpnonce: utils.get_nonce(),
            }, success: function (response) {
                if (response.success) {
                    _this_parent.notyf.removeAll();
                    const preSignedURL = response?.pre_signed_url;
                    if (type === 'preview') {
                        _this_parent.notyf.removeAll();
                        fancybox.openImage(preSignedURL);
                    } else if (type === 'download') {
                        window.open(preSignedURL, '_blank');
                    }
                } else {
                    _this_parent.notyf.removeAll();
                    _this_parent.notyf.error(msg.file_error);
                }
            }, error: function (request) {
                _this_parent.notyf.removeAll();
                _this_parent.notyf.error(_this_parent.getErrorMessage(request));
            }
        });
    }

    initDropzone(root = document) {
        const _this = this;
        const $root = root ? $(root) : $(document);

        $root.find(s.dropZone).each((index, el) => {
            // Avoid "Dropzone already attached" by skipping elements that already have an instance
            if (el.dropzone) return;

            let drop = new Dropzone(el, {
                acceptedFiles: 'image/*,application/pdf',
                // Scope clickable trigger to this dropzone element
                clickable: el.querySelector(s.triggerUpload),
                disablePreviews: false,
                // Elementet er en div (en form kan ikke ligge inde i
                // ticket-formen), så url kan ikke længere aflæses af et
                // action-attribut. Værdien spejler den gamle action="/" og
                // bruges aldrig — upload går gennem AWS-flowet herunder.
                url: '/',
            });

            drop.on("addedfile", file => {
                _this.uploadFile(file, drop);
            });
        });
    }

    getErrorMessage(response, fallback = msg.file_error) {
        if (typeof response?.error === 'string' && response.error.trim()) {
            return response.error;
        }

        if (typeof response?.message === 'string' && response.message.trim()) {
            return response.message;
        }

        if (typeof response?.responseJSON?.error === 'string' && response.responseJSON.error.trim()) {
            return response.responseJSON.error;
        }

        const brugerbesked = userMessage(response?.responseJSON?.data);
        if (brugerbesked) {
            return brugerbesked;
        }

        // `responseText` er den RÅ svarkrop — en stak, en HTML-fejlside, hvad
        // serveren nu returnerede. Den er aldrig skrevet til brugeren.

        return fallback;
    }

    removeFailedFile(drop, file) {
        if (drop && typeof drop.removeFile === 'function') {
            drop.removeFile(file);
        }
    }

    getUploadContext(file, ticketState) {
        const key = [
            ticketState.firebaseId,
            String(file.name || ''),
            Number(file.size) || 0,
            Number(file.lastModified) || 0,
            String(file.type || ''),
        ].join(':');
        const storedContext = this.pendingUploadContexts.get(key);
        if (storedContext) {
            return storedContext;
        }

        const context = {
            attachment_id: createSupportTicketMutationId(),
            firebase_id: ticketState.firebaseId,
            expected_revision: ticketState.revision,
            key,
            mutation_id: createSupportTicketMutationId(),
            page_post_id: utils.get_post_id(),
            uploaded_timestamp: Math.floor(Date.now() / 1000),
        };
        this.pendingUploadContexts.set(key, context);
        return context;
    }

    async uploadFile(file, drop) {
        const scope = drop.element;
        if (this.isSupportTicketFormSaving(scope)) {
            this.removeFailedFile(drop, file);
            this.notyf.info(msg.loading);
            return;
        }

        const ticketState = this.getTicketState(scope);
        if (!ticketState) {
            this.removeFailedFile(drop, file);
            this.notyf.error(msg.file_error);
            return;
        }

        const popupSave = capturePopupSave(ticketState.table);
        try {
            await supportTicketMutationQueue.enqueue(
                ticketState.firebaseId,
                () => this.uploadFileForTicket(file, drop, ticketState.firebaseId, scope, popupSave)
            );
        } catch (error) {
            this.notyf.removeAll();
            this.removeFailedFile(drop, file);
            this.notyf.error(this.getErrorMessage(error));
        }
    }

    async uploadFileForTicket(file, drop, firebaseId, scope, popupSave) {
        const ticketState = this.getTicketState(scope);
        if (!ticketState || ticketState.firebaseId !== firebaseId) {
            this.removeFailedFile(drop, file);
            this.notyf.error(msg.file_error);
            return;
        }

        const uploadContext = this.getUploadContext(file, ticketState);
        const form = new FormData();
        form.append("file", file);
        form.append('action', a.wl_upload_file_data);
        form.append('attachment_id', uploadContext.attachment_id);
        form.append('firebase_id', uploadContext.firebase_id);
        form.append('expected_revision', uploadContext.expected_revision);
        form.append('mutation_id', uploadContext.mutation_id);
        form.append('page_post_id', uploadContext.page_post_id);
        form.append('uploaded_timestamp', uploadContext.uploaded_timestamp);
        form.append('_wpnonce', utils.get_nonce());

        this.notyf.info(msg.file_accept);
        let response;
        try {
            response = await this.requestWithRetry({
                url: utils.get_ajax_url(),
                type: 'POST',
                data: form,
                contentType: false,
                processData: false,
            });
        } catch (request) {
            this.notyf.removeAll();
            if (!this.isRetryableRequestFailure(request)) {
                this.pendingUploadContexts.delete(uploadContext.key);
            }
            this.removeFailedFile(drop, file);
            this.notyf.error(this.getErrorMessage(request));
            return;
        }

        this.notyf.removeAll();
        if (this.isValidTicketMutationResponse(response, uploadContext, 'upsert_attachment')) {
            this.pendingUploadContexts.delete(uploadContext.key);
            this.applyTicketMutationState(response, ticketState.table, popupSave);
            const tableRowHtml = response?.table_row_html;
            if (tableRowHtml) {
                ticketState.table.find('tbody').prepend(tableRowHtml);
            }
            return;
        }

        if (response?.success === false) {
            this.pendingUploadContexts.delete(uploadContext.key);
        }
        this.removeFailedFile(drop, file);
        this.notyf.error(this.getErrorMessage(response));
    }
}
